import React, { useEffect, useState } from 'react'
import {
  Col,
  DatePicker,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  TimePicker,
} from 'antd'
import { leagueEventStore } from 'state/league/leagueEvent'
import { leaguePracticeStore } from 'state/league/leaguePractice'
import { myStore } from 'state/store'
import Location from 'pages/Events/Views/CreateLeague/screens/Step1/InputComps/Location'
import VenueModal from 'pages/Events/Views/CreateEvent/Screens/GeneralDetails/GeneralDetailModal/VenueModal'
import { Address } from 'types/globalTypes'
import { OrganizationVenues } from 'types/organization'
import { CreateLeaguePracticePayload } from 'types/league/leaguePractice'
import { LeagueTeamResp } from 'types/team/leagueTeam'
import { useGetLeagueTeam } from 'api/Team/LeagueTeam'
import {
  useCreateLeaguePractice,
  useUpdateLeaguePractice,
} from 'api/league/LeaguePractice/leaguePractice'
import {
  convertTimeToUtcMinutes,
  formValidationOnRequiredFields,
} from 'helper/utility'
import { infoMessage } from 'api/global'
import CustomAvatar from 'components/global/CustomAvatar'
import Spinner from 'components/Spinner'
import moment from 'moment'

const DURATION_OPTIONS = [
  { label: '30 min', value: 30 },
  { label: '45 min', value: 45 },
  { label: '60 min', value: 60 },
  { label: '90 min', value: 90 },
  { label: '120 min', value: 120 },
]

interface AddPracticeFormProps {
  form: FormInstance
}

const AddPracticeForm: React.FC<AddPracticeFormProps> = ({ form }) => {
  const { leagueData } = leagueEventStore(store => ({
    leagueData: store.leagueData,
  }))
  const { setAddPracticeModal, selectedPractice, setSelectedPractice } =
    leaguePracticeStore(store => ({
      setAddPracticeModal: store.setAddPracticeModal,
      selectedPractice: store.selectedPractice,
      setSelectedPractice: store.setSelectedPractice,
    }))
  const setModalBtnLoader = myStore(store => store.setModalBtnLoader)

  const [mapData, setMapModalData] = useState<Address | undefined>(
    selectedPractice ? selectedPractice.locationId : undefined,
  )
  const [venueModalVisible, setVenueModalVisible] = useState(false)
  const [venueDataItem, setVenueDataItem] = useState<OrganizationVenues | null>(
    selectedPractice ? selectedPractice.venueId : null,
  )

  const {
    data: leagueTeams,
    isLoading: teamsLoading,
    isFetching: teamsFetching,
    refetch: refetchTeams,
  } = useGetLeagueTeam(leagueData._id)

  useEffect(() => {
    refetchTeams()
  }, [])

  const formSchema = {
    date: selectedPractice ? moment(selectedPractice.date) : null,
    time: selectedPractice ? moment(selectedPractice.date) : null,
    duration: selectedPractice ? selectedPractice.duration : 60,
    leagueTeamId: selectedPractice ? selectedPractice.leagueTeam._id : null,
    courtFieldNumber: selectedPractice ? selectedPractice.courtFieldNumber : null,
  }

  const { mutate: createLeaguePractice } = useCreateLeaguePractice()
  const { mutate: updateLeaguePractice } = useUpdateLeaguePractice()

  const handleFinish = (values: any) => {
    if (
      !formValidationOnRequiredFields(
        values,
        ['date', 'time', 'duration', 'leagueTeamId'],
        {
          date: 'Date is required',
          time: 'Time is required',
          duration: 'Duration is required',
          leagueTeamId: 'Team is required',
        },
      )
    )
      return

    if (!mapData?._id) return infoMessage('Address is required')
    if (!venueDataItem?._id) return infoMessage('Venue is required')

    const isoDate = moment
      .utc(
        `${moment(values.date).format('YYYY-MM-DD')} ${moment
          .utc(values.time)
          .format('HH:mm')}`,
      )
      .format()

    setModalBtnLoader(true)

    if (selectedPractice) {
      updateLeaguePractice(
        {
          payload: {
            leagueTeamId: values.leagueTeamId ?? selectedPractice.leagueTeam._id,
            duration: values.duration,
            locationId: mapData._id,
            venueId: venueDataItem._id,
            courtFieldNumber: values.courtFieldNumber || undefined,
            date: isoDate,
          },
          PracticeId: selectedPractice._id,
        },
        {
          onSuccess: () => {
            setSelectedPractice(null)
            setModalBtnLoader(false)
            setAddPracticeModal(false)
          },
          onError: () => setModalBtnLoader(false),
        },
      )
    } else {
      const payload: CreateLeaguePracticePayload = {
        leagueId: leagueData._id,
        organizationId: leagueData.organizationId,
        duration: values.duration,
        leagueTeamId: values.leagueTeamId,
        locationId: mapData._id,
        venueId: venueDataItem._id,
        courtFieldNumber: values.courtFieldNumber || undefined,
        date: isoDate,
      }
      createLeaguePractice(payload, {
        onSuccess: () => {
          setModalBtnLoader(false)
          setAddPracticeModal(false)
        },
        onError: () => setModalBtnLoader(false),
      })
    }
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        initialValues={formSchema}
        onFinish={handleFinish}
      >
        <Row gutter={20}>
          <Col span={12}>
            <label className="input-label">Date</label>
            <Form.Item name="date">
              <DatePicker
                className="w-100 form-control-field"
                placeholder="MM/DD/YYYY"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <label className="input-label">Time</label>
            <Form.Item name="time">
              <TimePicker
                format="hh:mm a"
                className="w-100 form-control-field"
                placeholder="08:00 AM"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={20}>
          <Col span={12}>
            <label className="input-label required-input">Duration</label>
            <Form.Item name="duration">
              <Select
                className="h-50px select-field"
                placeholder="Select duration"
                options={DURATION_OPTIONS}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <label className="input-label required-input">Team</label>
            <Form.Item name="leagueTeamId">
              <Select
                className="h-50px select-field"
                placeholder="Select"
                loading={teamsLoading || teamsFetching}
                notFoundContent={
                  teamsLoading || teamsFetching ? <Spinner /> : 'No teams found'
                }
              >
                {(leagueTeams || []).map((team: LeagueTeamResp) => (
                  <Select.Option key={team._id} value={team._id}>
                    <div className="flex align-center gap-10px">
                      <CustomAvatar
                        size={20}
                        src={team.team.profilePhoto?.thumb}
                        name={team.team.name}
                      />
                      <p className="text-14 w-600">{team.team.name}</p>
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {leagueData.organizationId && (
          <Row>
            <Col span={24}>
              <Location
                selectedOrganization={{ id: leagueData.organizationId }}
                mapData={mapData}
                setMapModalData={setMapModalData}
                form={form}
              />
            </Col>
          </Row>
        )}

        {mapData && (
          <Row className="mt-20px">
            <Col span={24}>
              <label className="input-label required-input">Venue</label>
              <div
                className="w-full border-1px radius-10 flex align-center px-12px pointer h-50px"
                onClick={() => setVenueModalVisible(true)}
              >
                {venueDataItem ? (
                  <p className="text-gray">{venueDataItem.name}</p>
                ) : (
                  <p className="text-gray">Select</p>
                )}
              </div>
            </Col>
          </Row>
        )}

        <Row gutter={20} className="mt-20px">
          <Col span={24}>
            <label className="input-label">Court / field number</label>
            <Form.Item name="courtFieldNumber">
              <Input
                type="number"
                min={0}
                placeholder="optional"
                className="br-12"
                onWheel={e => e.currentTarget.blur()}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {venueModalVisible && (
        <VenueModal
          mapData={leagueData.location}
          setVenueModalVisible={setVenueModalVisible}
          setFieldsValue={() => {}}
          organizationId={leagueData?.organizationId}
          setVenueDataItem={setVenueDataItem}
        />
      )}
    </>
  )
}

export default AddPracticeForm
